import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '../ui/label';
import { Textarea } from '@/components/ui/Textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/Select';
import { Badge } from '@/components/ui/Badge';
import { Progress } from '../ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Treemap, Cell } from 'recharts';
import { Plus, Target, TrendingUp, Users, Calendar, CheckCircle, AlertCircle, Clock, Edit, Trash2, Eye, Loader2, Rocket, FileText, RefreshCw } from 'lucide-react';
import type { OKRProgressAnalytics } from '@/types/performance';

// 🚀 NEW: TanStack Query hooks for server state
import { useOKRProgress } from '@/hooks/queries'

// 🎨 NEW: Zustand UI store for client state
import { useOKRUIStore } from '@/stores/okr-ui-store'

const OKRs = () => {
  // 🚀 Server State (TanStack Query)
  const {
    data: okrData,
    isLoading,
    isFetching,
    error: queryError,
  } = useOKRProgress()

  // 🎨 UI State (Zustand)
  const {
    activeTab,
    setActiveTab,
    activeView,
    setActiveView,
    isDialogOpen,
    openDialog,
    closeDialog,
    selectedOKRId,
    setSelectedOKRId,
  } = useOKRUIStore()

  // ✅ No more manual useEffect for fetching OKRs - handled by TanStack Query!

  const error = queryError ? 'Failed to load OKRs. Please try again.' : null

  // NOTE: Previously had 280+ lines of hardcoded OKR data (removed - lines 45-326 deleted)
  // Now fetches real data from API via getOKRProgress()
  // Mapping API data structure to component's expected format

  const okrsBase = okrData ? Object.fromEntries(
    okrData.okrs.map((item, idx) => [
      `okr-${idx}`,
      {
        title: item.okr.title,
        okrs: item.keyResults.map(kr => ({
          id: kr.kr.krId,
          criterio: kr.kr.title,
          objetivo: `Progress: ${kr.currentValue} / ${kr.targetValue}`,
          tipo: 'quantitativo',
          acoesPraticas: [],
          progresso: kr.completionRate,
          status: kr.status === 'on-track' ? 'no-prazo' : kr.status === 'at-risk' ? 'atrasado' : kr.status === 'behind' ? 'atrasado' : 'concluido'
        }))
      }
    ])
  ) : {};

  // Fallback for empty data - removed 260+ lines of hardcoded OKRs here

  // Dados para visualizações
  const dadosEvolutivos = [
    { mes: 'Jan', progresso: 45 },
    { mes: 'Fev', progresso: 52 },
    { mes: 'Mar', progresso: 60 },
    { mes: 'Abr', progresso: 68 },
    { mes: 'Mai', progresso: 75 },
    { mes: 'Jun', progresso: 78 }
  ];

  const distribuicaoStatus = [
    { status: 'Concluído', quantidade: 8, color: '#10b981' },
    { status: 'No Prazo', quantidade: 12, color: '#3b82f6' },
    { status: 'Em Progresso', quantidade: 10, color: '#f59e0b' },
    { status: 'Atrasado', quantidade: 3, color: '#ef4444' }
  ];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'concluido': return <CheckCircle className="h-4 w-4 text-brand-primary" />;
      case 'no-prazo': return <Clock className="h-4 w-4 text-brand-blue" />;
      case 'em-progresso': return <TrendingUp className="h-4 w-4 text-warning" />;
      case 'atrasado': return <AlertCircle className="h-4 w-4 text-error" />;
      default: return <Clock className="h-4 w-4 text-brand-gray" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'concluido': return 'bg-brand-primary/20 text-success border-brand-primary';
      case 'no-prazo': return 'bg-brand-blue/20 text-brand-blue border-brand-blue';
      case 'em-progresso': return 'bg-warning/20 text-warning border-warning';
      case 'atrasado': return 'bg-error/20 text-error border-error';
      default: return 'bg-brand-gray/30/20 text-brand-gray border-brand-gray/30';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'concluido': return 'Concluído';
      case 'no-prazo': return 'No Prazo';
      case 'em-progresso': return 'Em Progresso';
      case 'atrasado': return 'Atrasado';
      default: return 'Não Definido';
    }
  };

  const getAllOKRs = () => {
    return Object.values(okrsBase).flatMap(tema => 
      tema.okrs.map(okr => ({ ...okr, tema: tema.title }))
    );
  };

  const renderFormularioAcao = () => (
    <Dialog open={isDialogOpen} onOpenChange={(open) => open ? openDialog() : closeDialog()}>
      <DialogContent className="bg-brand-dark-light border-brand-gray/10 text-white max-w-2xl">
        <DialogHeader>
          <DialogTitle>Adicionar Ação Prática</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-white">Título da Ação</Label>
            <Input className="bg-brand-dark-lighter border-brand-gray/20 text-white" placeholder="Ex: Implementar mentoria semanal" />
          </div>
          <div>
            <Label className="text-white">Descrição</Label>
            <Textarea className="bg-brand-dark-lighter border-brand-gray/20 text-white" placeholder="Descreva como esta ação será executada..." />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-white">Tipo</Label>
              <Select>
                <SelectTrigger className="bg-brand-dark-lighter border-brand-gray/20 text-white">
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="quantitativo">Quantitativo</SelectItem>
                  <SelectItem value="qualitativo">Qualitativo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white">Prazo</Label>
              <Input type="date" className="bg-brand-dark-lighter border-brand-gray/20 text-white" />
            </div>
          </div>
          <div>
            <Label className="text-white">Meta/Indicador</Label>
            <Input className="bg-brand-dark-lighter border-brand-gray/20 text-white" placeholder="Ex: 3 mentorias por mês" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={closeDialog}>
              Cancelar
            </Button>
            <Button className="bg-brand-primary text-black hover:bg-success" onClick={closeDialog}>
              Salvar Ação
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );

  const renderLista = () => (
    <div className="space-y-6">
      {Object.entries(okrsBase).map(([temaKey, tema]) => (
        <Card key={temaKey} className="bg-brand-dark-light border-brand-gray/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Target className="h-5 w-5 text-brand-primary" />
              {tema.title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {tema.okrs.map((okr) => (
                <div key={okr.id} className="border border-brand-gray/20 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {getStatusIcon(okr.status)}
                        <Badge variant="outline" className={getStatusColor(okr.status)}>
                          {getStatusLabel(okr.status)}
                        </Badge>
                        <Badge variant="outline" className={
                          okr.tipo === 'quantitativo' 
                            ? 'bg-brand-blue/20 text-brand-blue border-brand-blue' 
                            : 'bg-brand-primary/20 text-brand-primary border-brand-primary'
                        }>
                          {okr.tipo === 'quantitativo' ? 'Quantitativo' : 'Qualitativo'}
                        </Badge>
                      </div>
                      <p className="text-white text-sm mb-2 font-medium">{okr.criterio}</p>
                      <p className="text-brand-gray text-sm">{okr.objetivo}</p>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <Button size="sm" variant="outline" className="border-brand-gray/20">
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="outline" className="border-brand-gray/20">
                        <Edit className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <div className="flex justify-between mb-1">
                      <span className="text-brand-gray text-sm">Progresso</span>
                      <span className="text-white text-sm">{okr.progresso}%</span>
                    </div>
                    <Progress value={okr.progresso} className="h-2" />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-brand-gray text-sm">
                      Ações práticas: {okr.acoesPraticas.length}
                    </span>
                    <Button
                      size="sm"
                      className="bg-brand-primary text-black hover:bg-success"
                      onClick={openDialog}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Adicionar Ação
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
      {renderFormularioAcao()}
    </div>
  );

  const renderArvore = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {Object.entries(okrsBase).map(([temaKey, tema]) => (
          <Card key={temaKey} className="bg-brand-dark-light border-brand-gray/10">
            <CardHeader>
              <CardTitle className="text-white text-center">{tema.title}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {tema.okrs.map((okr) => (
                  <div key={okr.id} className="relative">
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-3 h-3 rounded-full ${
                        okr.status === 'concluido' ? 'bg-brand-primary' :
                        okr.status === 'no-prazo' ? 'bg-brand-blue' :
                        okr.status === 'em-progresso' ? 'bg-warning' : 'bg-error'
                      }`}></div>
                      <span className="text-white text-sm font-medium truncate">
                        {okr.criterio}
                      </span>
                    </div>
                    <div className="ml-5">
                      <Progress value={okr.progresso} className="h-1 mb-1" />
                      <span className="text-brand-gray text-xs">{okr.progresso}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );

  const renderEvolutiva = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-brand-dark-light border-brand-gray/10">
          <CardHeader>
            <CardTitle className="text-white">Evolução do Progresso</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={dadosEvolutivos}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="mes" stroke="#9ca3af" />
                <YAxis stroke="#9ca3af" domain={[0, 100]} />
                <Tooltip />
                <Line type="monotone" dataKey="progresso" stroke="#10b981" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="bg-brand-dark-light border-brand-gray/10">
          <CardHeader>
            <CardTitle className="text-white">Distribuição por Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {distribuicaoStatus.map((item) => (
                <div key={item.status} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full" style={{ backgroundColor: item.color }}></div>
                    <span className="text-white">{item.status}</span>
                  </div>
                  <Badge variant="outline" className="border-brand-gray/20 text-white">
                    {item.quantidade}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-brand-dark-light border-brand-gray/10">
        <CardHeader>
          <CardTitle className="text-white">Progresso por Tema</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={Object.entries(okrsBase).map(([key, tema]) => ({
              tema: tema.title,
              progresso: Math.round(tema.okrs.reduce((acc, okr) => acc + okr.progresso, 0) / tema.okrs.length)
            }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="tema" stroke="#9ca3af" angle={-45} textAnchor="end" height={80} />
              <YAxis stroke="#9ca3af" domain={[0, 100]} />
              <Tooltip />
              <Bar dataKey="progresso" fill="#10b981" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-brand-primary mx-auto mb-4" />
          <p className="text-brand-gray">Loading OKRs...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <Card className="bg-brand-dark-light border-brand-gray/10 max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-12 w-12 text-error mx-auto mb-4" />
            <h3 className="text-white text-lg font-medium mb-2">Error Loading OKRs</h3>
            <p className="text-brand-gray mb-4">{error}</p>
            <Button
              onClick={() => window.location.reload()}
              className="bg-brand-primary text-black hover:bg-success"
            >
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // No data state
  if (!okrData || !okrData.okrs || okrData.okrs.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-white text-2xl font-medium">Gestão de OKRs</h1>
            <p className="text-brand-gray">Objetivos e Resultados-Chave por critério de avaliação</p>
          </div>
        </div>
        <Card className="bg-brand-dark-light border-brand-gray/10">
          <CardContent className="pt-0">
            <div className="flex flex-col items-center justify-center py-16 px-4">
              {/* Icon with glow */}
              <div className="relative mb-6">
                <div className="absolute inset-0 bg-brand-primary/20 rounded-full blur-2xl animate-pulse"></div>
                <Rocket className="h-16 w-16 text-brand-primary relative z-10" />
              </div>

              {/* Heading */}
              <h3 className="text-xl font-semibold text-white mb-3 text-center">
                Comece sua jornada de OKRs
              </h3>

              {/* Supporting text */}
              <p className="text-brand-gray text-center max-w-md mb-8 text-sm">
                Os OKRs (Objectives and Key Results) ajudam a alinhar objetivos estratégicos com resultados mensuráveis. Defina metas claras, acompanhe o progresso em tempo real e alcance resultados excepcionais com sua equipe.
              </p>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-3 mb-8">
                <Button
                  onClick={openDialog}
                  className="bg-brand-primary text-black hover:bg-success font-medium"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Criar Primeiro OKR
                </Button>
                <Button
                  variant="outline"
                  className="border-brand-gray/20 text-white hover:bg-brand-dark-lighter font-medium"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Ver Exemplo
                </Button>
              </div>

              {/* Tip */}
              <div className="text-center text-sm text-brand-gray">
                Dica: Um bom OKR é ambicioso, mensurável e tem prazo definido
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-white text-2xl font-medium">Gestão de OKRs</h1>
          <p className="text-brand-gray">Objetivos e Resultados-Chave por critério de avaliação</p>
        </div>
        <Button className="bg-brand-primary text-black hover:bg-success" onClick={openDialog}>
          <Plus className="h-4 w-4 mr-2" />
          Nova Ação
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={(value: string) => setActiveTab(value as 'lista' | 'visualizacoes' | 'cadastro')}>
        <TabsList className="grid w-full grid-cols-3 bg-brand-dark-light">
          <TabsTrigger value="lista" className="text-white data-[state=active]:bg-brand-primary data-[state=active]:text-black">
            Lista de OKRs
          </TabsTrigger>
          <TabsTrigger value="visualizacoes" className="text-white data-[state=active]:bg-brand-primary data-[state=active]:text-black">
            Visualizações
          </TabsTrigger>
          <TabsTrigger value="cadastro" className="text-white data-[state=active]:bg-brand-primary data-[state=active]:text-black">
            Cadastro de Ações
          </TabsTrigger>
        </TabsList>

        <TabsContent value="lista">
          <div className="mb-4">
            <div className="flex gap-2">
              <Button 
                variant={activeView === 'lista' ? 'default' : 'outline'} 
                size="sm"
                onClick={() => setActiveView('lista')}
                className={activeView === 'lista' ? 'bg-brand-primary text-black' : 'border-brand-gray/20 text-white'}
              >
                Lista
              </Button>
              <Button 
                variant={activeView === 'arvore' ? 'default' : 'outline'} 
                size="sm"
                onClick={() => setActiveView('arvore')}
                className={activeView === 'arvore' ? 'bg-brand-primary text-black' : 'border-brand-gray/20 text-white'}
              >
                Árvore
              </Button>
            </div>
          </div>
          
          {activeView === 'lista' ? renderLista() : renderArvore()}
        </TabsContent>

        <TabsContent value="visualizacoes">
          {renderEvolutiva()}
        </TabsContent>

        <TabsContent value="cadastro">
          <Card className="bg-brand-dark-light border-brand-gray/10">
            <CardHeader>
              <CardTitle className="text-white">Cadastrar Nova Ação Prática</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white">OKR Relacionado</Label>
                    <Select>
                      <SelectTrigger className="bg-brand-dark-lighter border-brand-gray/20 text-white">
                        <SelectValue placeholder="Selecione um OKR" />
                      </SelectTrigger>
                      <SelectContent>
                        {getAllOKRs().map((okr) => (
                          <SelectItem key={okr.id} value={okr.id}>
                            {okr.tema} - {okr.criterio}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-white">Responsável</Label>
                    <Select>
                      <SelectTrigger className="bg-brand-dark-lighter border-brand-gray/20 text-white">
                        <SelectValue placeholder="Selecione o responsável" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="joao">João Silva</SelectItem>
                        <SelectItem value="maria">Maria Santos</SelectItem>
                        <SelectItem value="pedro">Pedro Oliveira</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div>
                  <Label className="text-white">Título da Ação</Label>
                  <Input className="bg-brand-dark-lighter border-brand-gray/20 text-white" placeholder="Ex: Criar programa de mentoria técnica" />
                </div>
                
                <div>
                  <Label className="text-white">Descrição Detalhada</Label>
                  <Textarea className="bg-brand-dark-lighter border-brand-gray/20 text-white" rows={4} placeholder="Descreva como esta ação será executada, recursos necessários, etc..." />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-white">Tipo</Label>
                    <Select>
                      <SelectTrigger className="bg-brand-dark-lighter border-brand-gray/20 text-white">
                        <SelectValue placeholder="Tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="quantitativo">Quantitativo</SelectItem>
                        <SelectItem value="qualitativo">Qualitativo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-white">Data de Início</Label>
                    <Input type="date" className="bg-brand-dark-lighter border-brand-gray/20 text-white" />
                  </div>
                  <div>
                    <Label className="text-white">Data de Conclusão</Label>
                    <Input type="date" className="bg-brand-dark-lighter border-brand-gray/20 text-white" />
                  </div>
                </div>
                
                <div>
                  <Label className="text-white">Meta/Indicador de Sucesso</Label>
                  <Input className="bg-brand-dark-lighter border-brand-gray/20 text-white" placeholder="Ex: Realizar 3 mentorias por mês para toda a equipe" />
                </div>
                
                <div>
                  <Label className="text-white">Recursos Necessários</Label>
                  <Textarea className="bg-brand-dark-lighter border-brand-gray/20 text-white" placeholder="Descreva recursos humanos, materiais, orçamento, etc..." />
                </div>
                
                <div className="flex justify-end gap-4">
                  <Button variant="outline" className="border-brand-gray/20 text-white hover:bg-brand-dark-lighter">
                    Cancelar
                  </Button>
                  <Button className="bg-brand-primary text-black hover:bg-success">
                    Salvar Ação
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default OKRs;